import { InfoRounded } from "@mui/icons-material";
import { Box, Divider, Grid, Typography } from "@mui/material";
import { isObject } from "formik";
import pluralize from "pluralize";
import { useContext } from "react";
import useColors from "../../../../api/hooks/useColors";
import CustomToolTip from "../../../../components/CustomTooltip";
import LoadingIcon from "../../../../components/LoadingIcon";
import { EnvFeatureFlagContext } from "../../../../contexts/EnvFeaturesContext";
import SvgToIcon from "../../../../icons/SvgToIcon";
import {
  daysPastSince,
  getPrettyDateTime,
  useLocalTime,
} from "../../../../utils/timestamp";
import ChipColor from "../../../remediation/plan/components/ChipColor";
import { mapBreezeScoreToSeverity } from "../VulnerabilitiesTable";
import { VulnerabilityExploit } from "../VulnerabilitiesView";

const VulnerabilityMetadata = ({
  selectedVulnerability,
  isLoadingMisconfigured,
  complexMisconfiguredEntitiesByType,
  colors,
  isShowWarningIcon,
  icons,
  haveInternetFacingEntities,
}) => {
  const showLocalTime = useLocalTime();
  const { isActiveRds } = useContext(EnvFeatureFlagContext);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      {/* First row - all metadata except Published Date */}
      <Grid
        container
        columns={14}
        sx={{
          textAlign: "left",
          position: "relative",
        }}
      >
        {/* Breeze Score */}
        {/* hide for newscorp */}
        {!isActiveRds && (
          <Grid item xs={2.2}>
            <MetadataItem
              label={
                <CustomToolTip
                  title={
                    "Calculates the priority and severity of vulnerabilities, factoring in the ease of remediation, existence of compensating controls, and the risk from multiple sources."
                  }
                >
                  Breeze Score
                </CustomToolTip>
              }
            >
              <ChipColor
                value={selectedVulnerability?.breeze_score?.total?.toFixed(1)}
                type={"vulnerabilitySeverity"}
                keyMapping={mapBreezeScoreToSeverity}
                useValueAsLabel={true}
              />
            </MetadataItem>
          </Grid>
        )}

        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

        {/* Severity */}
        <Grid item xs={2.2}>
          <MetadataItem
            label={<CustomToolTip title={"Severity"}>Severity</CustomToolTip>}
          >
            <ChipColor
              value={selectedVulnerability?.severity}
              type={"vulnerabilitySeverity"}
            />
          </MetadataItem>
        </Grid>

        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

        {/* Fix */}
        <Grid item xs={2}>
          <MetadataItem label="Has Fix">
            <Box sx={{ mb: "1px" }}>
              {selectedVulnerability?.has_fix === undefined ? (
                <Typography variant="caption">N/A</Typography>
              ) : selectedVulnerability?.has_fix ? (
                <Typography variant="caption">Available</Typography>
              ) : (
                <Typography variant="caption">N/A</Typography>
              )}
            </Box>
          </MetadataItem>
        </Grid>

        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

        {/* Exploit */}
        <Grid item xs={2}>
          <MetadataItem label="Has Exploit">
            <Box sx={{ mb: "1px" }}>
              <ExploitContent
                selectedVulnerability={selectedVulnerability}
                isShowWarningIcon={isShowWarningIcon}
                icons={icons}
              />
            </Box>
          </MetadataItem>
        </Grid>

        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

        {/* Internet Facing */}
        <Grid item xs={2}>
          <MetadataItem label="Internet Facing">
            <Box sx={{ mb: "1px" }}>
              {!isLoadingMisconfigured && complexMisconfiguredEntitiesByType ? (
                haveInternetFacingEntities() === "unknown" ? (
                  <Typography variant="caption">N/A</Typography>
                ) : haveInternetFacingEntities() ? (
                  <Typography variant="caption">Yes</Typography>
                ) : (
                  <Typography variant="caption">No</Typography>
                )
              ) : (
                <LoadingIcon size={5} />
              )}
            </Box>
          </MetadataItem>
        </Grid>

        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

        {/* Published Date */}
        <Grid item xs={2.2}>
          <MetadataItem label="Published Date">
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {isObject(selectedVulnerability?.published_date) &&
                selectedVulnerability?.published_date.hasOwnProperty(
                  "timestamp_utc"
                ) && (
                  <>
                    <Typography variant="caption">
                      {getPrettyDateTime(
                        selectedVulnerability?.published_date?.timestamp_utc,
                        showLocalTime
                      )}
                    </Typography>
                    <CustomToolTip
                      title={
                        <Typography variant="caption">
                          {" Exposed for "}
                          {daysPastSince(
                            selectedVulnerability?.published_date
                              ?.timestamp_utc_human_readable
                          )}
                          {pluralize(
                            " day",
                            daysPastSince(
                              selectedVulnerability?.published_date
                                ?.timestamp_utc_human_readable
                            )
                          )}
                        </Typography>
                      }
                    >
                      <InfoRounded
                        sx={{
                          fontSize: 16,
                          color: colors.text_secondary,
                          opacity: 0.48,
                        }}
                      />
                    </CustomToolTip>
                  </>
                )}
            </Box>
          </MetadataItem>
        </Grid>
      </Grid>
    </Box>
  );
};

const MetadataItem = ({ label, children }) => {
  const colors = useColors();
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        width: "100%",
        ml: 1,
      }}
    >
      <Box sx={{ width: "100%", mb: 1, textAlign: "left" }}>
        <Typography
          variant="caption"
          sx={{
            color: colors.text_secondary,
          }}
        >
          {label}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center" }}>{children}</Box>
    </Box>
  );
};

const ExploitContent = ({
  selectedVulnerability,
  isShowWarningIcon,
  icons,
}) => {
  let exploit = selectedVulnerability?.exploit;
  if (exploit === VulnerabilityExploit.HAS_EXPLOIT_IN_THE_WILD) {
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <Typography variant="caption">Available</Typography>
        <CustomToolTip
          title={
            <Typography variant="caption">
              This CVE is found in the wild
            </Typography>
          }
        >
          <Box>
            <SvgToIcon IconLogo={icons.exploitInTheWildIcon} width={"18px"} />
          </Box>
        </CustomToolTip>
      </Box>
    );
  }

  if (exploit === VulnerabilityExploit.AVAILABLE) {
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <Typography variant="caption">Available</Typography>
        {isShowWarningIcon() && (
          <CustomToolTip title="Among the top 20% longest-exposed vulnerabilities">
            <Box display="flex" alignItems="center">
              <SvgToIcon
                IconLogo={icons.severityIcon}
                style={{
                  width: "26px",
                }}
              />
            </Box>
          </CustomToolTip>
        )}
      </Box>
    );
  }

  return <Typography variant="caption">N/A</Typography>;
};

export default VulnerabilityMetadata;
